import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardHeader } from '../../components/Card.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'
import {
  listPlatformCosts,
  getPlatformCostsSummary,
  createPlatformCost,
  updatePlatformCost,
  deletePlatformCost,
  getPlatformAssumptions,
  updatePlatformAssumptions,
  listCostCategories,
  createCostCategory,
  isTimeBasedCategory,
  RATE_FIELD_FOR_SLUG,
} from '../../api/platformCosts.js'
import { listOrganizationsByType } from '../../api/organizations.js'

/**
 * /superuser/platform-costs — Phase 6.1
 *
 * Platform-admin-only surface for capturing ICER Health's own running
 * costs. Tabular list with date+category filters, a "+ Add cost" modal,
 * row edit/delete, and a small rates-editor (PlatformAssumption singleton)
 * tucked into a collapsible side card.
 *
 * Audience boundary: this page must never be reachable by org_admin
 * users. Route is gated in App.jsx via RequireRole roles=['admin'], and
 * the backend ViewSet additionally returns 403 to non-admins.
 */
export default function PlatformCostsPage() {
  const qc = useQueryClient()

  const [filters, setFilters] = useState({
    category: '',
    from: '',
    to: '',
    organization: '',
  })
  const [showAdd, setShowAdd] = useState(false)
  const [editing, setEditing] = useState(null)   // cost row being edited
  const [showRates, setShowRates] = useState(false)

  const params = useMemo(() => {
    const p = {}
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== '' && v !== null && v !== undefined) p[k] = v
    })
    return p
  }, [filters])

  // ---- Queries ----

  const listQ = useQuery({
    queryKey: ['platform-costs', params],
    queryFn: () => listPlatformCosts(params),
  })

  const summaryQ = useQuery({
    queryKey: ['platform-costs-summary', params],
    queryFn: () => getPlatformCostsSummary(params),
  })

  const ratesQ = useQuery({
    queryKey: ['platform-assumptions'],
    queryFn: getPlatformAssumptions,
  })

  // Organizations dropdown for the "attributable to" optional FK.
  // listOrganizationsByType() with no arg returns every active org —
  // already returns an unwrapped array (no .results envelope).
  const orgsQ = useQuery({
    queryKey: ['organizations-for-cost-attribution'],
    queryFn: () => listOrganizationsByType(),
    // Keep cached for the whole session — orgs don't change between cost entries.
    staleTime: 5 * 60 * 1000,
  })

  // CostCategory list (Phase 6.4 — dynamic). Used to populate the
  // filter <select> AND the form modal's category combobox. Active-only;
  // platform admin can manage retired categories via /admin/ if needed.
  const categoriesQ = useQuery({
    queryKey: ['cost-categories'],
    queryFn: () => listCostCategories({ includeInactive: false }),
    staleTime: 60 * 1000,
  })

  // ---- Mutations ----

  const createMut = useMutation({
    mutationFn: createPlatformCost,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform-costs'] })
      qc.invalidateQueries({ queryKey: ['platform-costs-summary'] })
      setShowAdd(false)
    },
  })

  const updateMut = useMutation({
    mutationFn: ({ id, patch }) => updatePlatformCost(id, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform-costs'] })
      qc.invalidateQueries({ queryKey: ['platform-costs-summary'] })
      setEditing(null)
    },
  })

  const deleteMut = useMutation({
    mutationFn: deletePlatformCost,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform-costs'] })
      qc.invalidateQueries({ queryKey: ['platform-costs-summary'] })
    },
  })

  const ratesMut = useMutation({
    mutationFn: ({ id, patch }) => updatePlatformAssumptions(id, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform-assumptions'] })
    },
  })

  // ---- Derived ----

  const rows = listQ.data?.results ?? listQ.data ?? []
  const totalRows = listQ.data?.count ?? rows.length
  const rates = (ratesQ.data?.results ?? ratesQ.data ?? [])[0] || null
  const orgs = orgsQ.data?.results ?? orgsQ.data ?? []
  const categories = categoriesQ.data || []

  function setFilter(key, value) {
    setFilters({ ...filters, [key]: value })
  }
  function clearFilters() {
    setFilters({ category: '', from: '', to: '', organization: '' })
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="Platform operating costs"
          subtitle={
            'ICER Health\'s running costs to deliver Wantok. Captures clinic time foregone, '
            + 'opportunity cost, meals, travel, infrastructure, and contractor spend. '
            + 'Editable by ICER cost-entry staff and platform admins; not visible to org admins.'
          }
          right={
            <div className="flex gap-2">
              <button
                onClick={() => setShowRates(true)}
                className="text-xs font-semibold tracking-wide px-3 py-1.5 rounded-full border border-sand-300 text-slate-600 hover:bg-sand-50 transition-colors"
              >
                ⚙ Rates
              </button>
              <button
                onClick={() => setShowAdd(true)}
                className="text-xs font-semibold tracking-wide px-3 py-1.5 rounded-full bg-wantok-600 text-white hover:bg-wantok-700 transition-colors"
              >
                + Add cost
              </button>
            </div>
          }
        />
      </Card>

      <SummaryStrip summary={summaryQ.data} />

      <FilterPanel
        filters={filters}
        orgs={orgs}
        categories={categories}
        onChange={setFilter}
        onClear={clearFilters}
      />

      {listQ.error && (
        <ErrorBanner error={listQ.error} onRetry={() => listQ.refetch()} />
      )}
      {listQ.isLoading && (
        <Card>
          <div className="text-sm text-slate-400">Loading costs…</div>
        </Card>
      )}

      {listQ.data && (
        <Card>
          <CardHeader
            title={`${totalRows.toLocaleString()} cost${totalRows === 1 ? '' : 's'}`}
            subtitle="Newest first. Click a row to edit or delete."
          />
          {rows.length === 0 ? (
            <div className="text-sm text-slate-400 italic py-6 text-center">
              No costs match the current filter. Add your first cost using the button above.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[11px] uppercase tracking-wide text-slate-500 border-b">
                  <tr>
                    <th className="text-left p-2 w-28">Date</th>
                    <th className="text-left p-2 w-44">Category</th>
                    <th className="text-right p-2 w-20">Hours</th>
                    <th className="text-right p-2 w-28">Amount (PGK)</th>
                    <th className="text-left p-2">Notes</th>
                    <th className="text-left p-2 w-40">Org (attributable)</th>
                    <th className="text-left p-2 w-28">Recorded by</th>
                    <th className="p-2 w-16"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((r) => (
                    <CostRow
                      key={r.id}
                      row={r}
                      onEdit={() => setEditing(r)}
                      onDelete={() => {
                        if (window.confirm(
                          `Delete this cost (K${r.amount_pgk} on ${r.incurred_on})?`
                        )) {
                          deleteMut.mutate(r.id)
                        }
                      }}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {showAdd && (
        <CostFormModal
          title="Add platform cost"
          rates={rates}
          orgs={orgs}
          categories={categories}
          isSubmitting={createMut.isPending}
          error={createMut.error}
          onSubmit={(payload) => createMut.mutate(payload)}
          onClose={() => { setShowAdd(false); createMut.reset() }}
        />
      )}

      {editing && (
        <CostFormModal
          title={`Edit cost from ${editing.incurred_on}`}
          rates={rates}
          orgs={orgs}
          categories={categories}
          initialValues={editing}
          isSubmitting={updateMut.isPending}
          error={updateMut.error}
          onSubmit={(payload) => updateMut.mutate({ id: editing.id, patch: payload })}
          onClose={() => { setEditing(null); updateMut.reset() }}
        />
      )}

      {showRates && (
        <RatesModal
          rates={rates}
          isSubmitting={ratesMut.isPending}
          error={ratesMut.error}
          onSubmit={(patch) => {
            if (!rates?.id) return
            ratesMut.mutate({ id: rates.id, patch }, {
              onSuccess: () => setShowRates(false),
            })
          }}
          onClose={() => { setShowRates(false); ratesMut.reset() }}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Summary strip — totals + per-category mini bars
// ---------------------------------------------------------------------------

function SummaryStrip({ summary }) {
  if (!summary) return null
  const total = summary.total_pgk || 0
  const hours = summary.hours_total || 0
  return (
    <Card>
      <div className="flex flex-wrap items-baseline gap-x-8 gap-y-3">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500">Total spend</div>
          <div className="text-2xl font-display font-semibold text-wantok-700">
            K{Number(total).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>
        {hours > 0 && (
          <div>
            <div className="text-[11px] uppercase tracking-wide text-slate-500">Hours</div>
            <div className="text-lg font-semibold text-slate-700">{Number(hours).toFixed(1)}</div>
          </div>
        )}
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500">Rows</div>
          <div className="text-lg font-semibold text-slate-700">{summary.count}</div>
        </div>
      </div>
      {summary.by_category?.length > 0 && (
        <div className="mt-4 space-y-2">
          {summary.by_category.map((c) => {
            const pct = total > 0 ? (c.total_pgk / total) * 100 : 0
            return (
              <div key={c.category} className="flex items-center gap-3 text-sm">
                <div className="w-44 truncate text-slate-600">{c.category_label}</div>
                <div className="flex-1 bg-sand-100 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-wantok-500 h-full rounded-full"
                    style={{ width: `${Math.min(100, pct)}%` }}
                  />
                </div>
                <div className="w-28 text-right tabular-nums text-slate-700">
                  K{Number(c.total_pgk).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="w-12 text-right text-[11px] text-slate-500">
                  {pct.toFixed(0)}%
                </div>
              </div>
            )
          })}
        </div>
      )}
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------

function FilterPanel({ filters, orgs, categories = [], onChange, onClear }) {
  const hasFilters = Object.values(filters).some((v) => v !== '')
  return (
    <Card>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
        <Field label="From">
          <input
            type="date"
            value={filters.from}
            onChange={(e) => onChange('from', e.target.value)}
            className="w-full border border-sand-300 rounded-md px-2 py-1.5 text-sm"
          />
        </Field>
        <Field label="To">
          <input
            type="date"
            value={filters.to}
            onChange={(e) => onChange('to', e.target.value)}
            className="w-full border border-sand-300 rounded-md px-2 py-1.5 text-sm"
          />
        </Field>
        <Field label="Category">
          <select
            value={filters.category}
            onChange={(e) => onChange('category', e.target.value)}
            className="w-full border border-sand-300 rounded-md px-2 py-1.5 text-sm bg-white"
          >
            <option value="">All categories</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </Field>
        <Field label="Attributable org">
          <select
            value={filters.organization}
            onChange={(e) => onChange('organization', e.target.value)}
            className="w-full border border-sand-300 rounded-md px-2 py-1.5 text-sm bg-white"
          >
            <option value="">Any (or platform-wide)</option>
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </Field>
      </div>
      {hasFilters && (
        <div className="mt-3 text-right">
          <button
            onClick={onClear}
            className="text-xs text-slate-500 hover:text-slate-700 underline"
          >Clear filters</button>
        </div>
      )}
    </Card>
  )
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

function CostRow({ row, onEdit, onDelete }) {
  return (
    <tr className="hover:bg-sand-50/50">
      <td className="p-2 whitespace-nowrap text-slate-700">{row.incurred_on}</td>
      <td className="p-2 text-slate-700">{row.category_label || row.category_slug || '—'}</td>
      <td className="p-2 text-right tabular-nums text-slate-600">
        {row.hours != null ? Number(row.hours).toFixed(1) : '—'}
      </td>
      <td className="p-2 text-right tabular-nums font-semibold text-slate-800">
        K{Number(row.amount_pgk).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </td>
      <td className="p-2 text-slate-600 max-w-md truncate" title={row.notes}>
        {row.notes || <span className="text-slate-300 italic">—</span>}
      </td>
      <td className="p-2 text-slate-600">
        {row.organization_name || <span className="text-slate-300 italic">Platform-wide</span>}
      </td>
      <td className="p-2 text-slate-500 text-xs">{row.recorded_by_username}</td>
      <td className="p-2 text-right">
        <button
          onClick={onEdit}
          className="text-xs text-wantok-700 hover:underline mr-2"
        >Edit</button>
        <button
          onClick={onDelete}
          className="text-xs text-rose-600 hover:underline"
        >Delete</button>
      </td>
    </tr>
  )
}

// ---------------------------------------------------------------------------
// Add / Edit modal
// ---------------------------------------------------------------------------

function CostFormModal({
  title, rates, orgs, categories = [],
  initialValues, isSubmitting, error, onSubmit, onClose,
}) {
  const qc = useQueryClient()
  const today = new Date().toISOString().slice(0, 10)

  // Phase 6.4: `category` is now a numeric FK id, not a slug string.
  // Default to the first active category (typically clinic_time_foregone)
  // when adding; preserve the existing id when editing.
  const defaultCategoryId = initialValues?.category != null
    ? initialValues.category
    : (categories[0]?.id ?? '')

  const [form, setForm] = useState(() => ({
    incurred_on:  initialValues?.incurred_on  || today,
    category:     defaultCategoryId,
    amount_pgk:   initialValues?.amount_pgk   || '',
    hours:        initialValues?.hours        || '',
    notes:        initialValues?.notes        || '',
    organization: initialValues?.organization || '',
  }))

  // "Add new category" inline mini-form state.
  const [showAddCat, setShowAddCat] = useState(false)
  const [newCatName, setNewCatName] = useState('')
  const [catError, setCatError] = useState(null)

  const addCatMut = useMutation({
    mutationFn: createCostCategory,
    onSuccess: (newCat) => {
      qc.invalidateQueries({ queryKey: ['cost-categories'] })
      // Auto-select the newly-created category.
      setForm((f) => ({ ...f, category: newCat.id }))
      setShowAddCat(false)
      setNewCatName('')
      setCatError(null)
    },
    onError: (err) => {
      // The platform's standard error envelope (core/exceptions.py)
      // wraps DRF validation errors as:
      //   { error: { code: 'validation_error',
      //              message: 'Validation failed.',
      //              details: { name: ['...already exists...'] } } }
      // We want to surface the specific field reason, not the generic
      // "Validation failed." top-level message — that one tells the
      // user nothing actionable.
      const r = err?.response?.data
      const d = r?.error?.details || r?.details || r
      // Walk through the most likely places the duplicate-name message
      // could land, in order of specificity.
      const pickFirst = (val) =>
        Array.isArray(val) ? val[0] : (typeof val === 'string' ? val : null)
      const fieldMsg =
        pickFirst(d?.name)
        || pickFirst(d?.slug)
        || pickFirst(d?.non_field_errors)
        || pickFirst(d?.detail)
      // Fall through to the envelope's generic message only if no
      // field-level message exists; that's better than nothing but
      // worse than the field-level reason.
      const friendly = fieldMsg
        || r?.error?.message
        || r?.detail
        || err?.message
        || 'Failed to create category.'
      const lower = String(friendly).toLowerCase()
      if (lower.includes('already exists') || lower.includes('unique')
          || lower.includes('must be unique')) {
        setCatError(
          `A category named "${newCatName}" already exists. Pick it from `
          + 'the dropdown above instead of adding a duplicate.'
        )
      } else {
        setCatError(friendly)
      }
    },
  })

  // Resolve the currently-selected category so the form can decide
  // whether to show the time-based hours input + apply the rate.
  const selectedCat = categories.find((c) => c.id === form.category) || null
  const slug = selectedCat?.slug || ''
  const timeBased = isTimeBasedCategory(slug)
  const rateField = RATE_FIELD_FOR_SLUG[slug] || null
  const rate = timeBased && rates && rateField
    ? Number(rates[rateField]) || 0
    : 0

  function setF(key, value) {
    setForm((f) => ({ ...f, [key]: value }))
  }

  // For time-based categories, auto-fill amount from hours × rate
  // when the user enters hours.
  function setHours(v) {
    if (timeBased && rate > 0) {
      const hoursNum = parseFloat(v) || 0
      const computed = (hoursNum * rate).toFixed(2)
      setForm((f) => ({ ...f, hours: v, amount_pgk: computed }))
    } else {
      setF('hours', v)
    }
  }

  function setCategory(v) {
    // v is the option value — either a numeric id (string) or the
    // sentinel "__add__" meaning "open the add-category mini-form".
    if (v === '__add__') {
      setShowAddCat(true)
      return
    }
    const newId = parseInt(v, 10) || ''
    const newCat = categories.find((c) => c.id === newId) || null
    const newSlug = newCat?.slug || ''
    const newTimeBased = isTimeBasedCategory(newSlug)
    const newRateField = RATE_FIELD_FOR_SLUG[newSlug] || null

    if (newTimeBased && rates && newRateField && form.hours) {
      const newRate = Number(rates[newRateField]) || 0
      const hoursNum = parseFloat(form.hours) || 0
      const computed = (hoursNum * newRate).toFixed(2)
      setForm((f) => ({ ...f, category: newId, amount_pgk: computed }))
    } else if (!newTimeBased) {
      setForm((f) => ({ ...f, category: newId, hours: '' }))
    } else {
      setF('category', newId)
    }
  }

  function submitNewCat(e) {
    e.preventDefault()
    const name = (newCatName || '').trim()
    if (!name) return
    addCatMut.mutate({ name })
  }

  function handleSubmit(e) {
    e.preventDefault()
    if (!form.category) {
      // Defence in depth — the <select> should always have something.
      return
    }
    const payload = {
      incurred_on: form.incurred_on,
      category:    parseInt(form.category, 10),
      amount_pgk:  parseFloat(form.amount_pgk),
      hours:       form.hours ? parseFloat(form.hours) : null,
      notes:       form.notes || '',
      organization: form.organization ? parseInt(form.organization) : null,
    }
    onSubmit(payload)
  }

  return (
    <Modal title={title} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-3">
        <Field label="Date incurred">
          <input
            type="date"
            value={form.incurred_on}
            onChange={(e) => setF('incurred_on', e.target.value)}
            required
            className="w-full border border-sand-300 rounded-md px-2 py-1.5 text-sm"
          />
        </Field>
        <Field label="Category">
          <select
            value={form.category}
            onChange={(e) => setCategory(e.target.value)}
            required
            className="w-full border border-sand-300 rounded-md px-2 py-1.5 text-sm bg-white"
          >
            {categories.length === 0 && (
              <option value="" disabled>Loading categories…</option>
            )}
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
            <option value="__add__">+ Add new category…</option>
          </select>
          {showAddCat && (
            <div className="mt-2 rounded-md border border-dashed border-wantok-300 bg-wantok-50/40 p-2 space-y-2">
              <div className="text-[11px] uppercase tracking-wide text-wantok-700">
                New category
              </div>
              <input
                type="text"
                value={newCatName}
                onChange={(e) => setNewCatName(e.target.value)}
                placeholder="e.g. Driver training, Community workshop"
                className="w-full border border-sand-300 rounded-md px-2 py-1.5 text-sm bg-white"
                autoFocus
              />
              {catError && (
                <div className="text-[11px] text-rose-700">{catError}</div>
              )}
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => { setShowAddCat(false); setNewCatName(''); setCatError(null) }}
                  className="text-xs text-slate-500 hover:text-slate-700 px-2 py-1"
                >Cancel</button>
                <button
                  type="button"
                  onClick={submitNewCat}
                  disabled={addCatMut.isPending || !newCatName.trim()}
                  className="text-xs font-semibold px-3 py-1 rounded-md bg-wantok-600 text-white hover:bg-wantok-700 disabled:opacity-50"
                >
                  {addCatMut.isPending ? 'Adding…' : 'Add'}
                </button>
              </div>
            </div>
          )}
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={timeBased ? `Hours @ K${rate.toFixed(2)}/hr` : 'Hours (optional)'}>
            <input
              type="number" step="0.25" min="0"
              value={form.hours}
              onChange={(e) => setHours(e.target.value)}
              placeholder="0.0"
              className="w-full border border-sand-300 rounded-md px-2 py-1.5 text-sm"
            />
          </Field>
          <Field label="Amount (PGK)">
            <input
              type="number" step="0.01" min="0"
              value={form.amount_pgk}
              onChange={(e) => setF('amount_pgk', e.target.value)}
              required
              className="w-full border border-sand-300 rounded-md px-2 py-1.5 text-sm font-semibold"
            />
          </Field>
        </div>
        {timeBased && rate > 0 && (
          <div className="text-[11px] text-slate-500 leading-relaxed">
            Hours × K{rate.toFixed(2)}/hr auto-computes amount.
            You can override by editing the amount field directly — the
            stored value is the amount, hours is informational.
          </div>
        )}
        <Field label="Notes">
          <textarea
            value={form.notes}
            onChange={(e) => setF('notes', e.target.value)}
            rows={3}
            placeholder="What was this cost for?"
            className="w-full border border-sand-300 rounded-md px-2 py-1.5 text-sm"
          />
        </Field>
        <Field label="Attributable to org (optional)">
          <select
            value={form.organization}
            onChange={(e) => setF('organization', e.target.value)}
            className="w-full border border-sand-300 rounded-md px-2 py-1.5 text-sm bg-white"
          >
            <option value="">Platform-wide (no specific org)</option>
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </Field>

        {error && (
          <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
            {error?.response?.data?.detail || error?.message || 'Save failed.'}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-3 py-1.5 rounded-md border border-sand-300 text-sm text-slate-600 hover:bg-sand-50"
          >Cancel</button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-3 py-1.5 rounded-md bg-wantok-600 text-white text-sm hover:bg-wantok-700 disabled:opacity-60"
          >{isSubmitting ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// Rates modal — edits the PlatformAssumption singleton
// ---------------------------------------------------------------------------

function RatesModal({ rates, isSubmitting, error, onSubmit, onClose }) {
  const [form, setForm] = useState(() => ({
    doctor_clinic_hourly_rate_pgk:   rates?.doctor_clinic_hourly_rate_pgk   || 200,
    doctor_hospital_hourly_rate_pgk: rates?.doctor_hospital_hourly_rate_pgk || 150,
    consultation_avg_minutes:        rates?.consultation_avg_minutes        || 15,
    daily_meal_allowance_pgk:        rates?.daily_meal_allowance_pgk        || 60,
    notes:                           rates?.notes                           || '',
  }))

  function setF(key, value) {
    setForm((f) => ({ ...f, [key]: value }))
  }

  function handleSubmit(e) {
    e.preventDefault()
    onSubmit({
      doctor_clinic_hourly_rate_pgk:   parseFloat(form.doctor_clinic_hourly_rate_pgk),
      doctor_hospital_hourly_rate_pgk: parseFloat(form.doctor_hospital_hourly_rate_pgk),
      consultation_avg_minutes:        parseInt(form.consultation_avg_minutes),
      daily_meal_allowance_pgk:        parseFloat(form.daily_meal_allowance_pgk),
      notes:                           form.notes,
    })
  }

  return (
    <Modal title="Default rates" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-3">
        <p className="text-xs text-slate-500 leading-relaxed">
          These rates auto-fill the cost-entry form when you enter hours.
          Historical entries keep their captured amounts even if you change
          the rate later.
        </p>
        <Field label="Doctor clinic hourly rate (PGK/hr)">
          <input
            type="number" step="1" min="0"
            value={form.doctor_clinic_hourly_rate_pgk}
            onChange={(e) => setF('doctor_clinic_hourly_rate_pgk', e.target.value)}
            className="w-full border border-sand-300 rounded-md px-2 py-1.5 text-sm"
          />
        </Field>
        <Field label="Doctor hospital hourly rate (PGK/hr)">
          <input
            type="number" step="1" min="0"
            value={form.doctor_hospital_hourly_rate_pgk}
            onChange={(e) => setF('doctor_hospital_hourly_rate_pgk', e.target.value)}
            className="w-full border border-sand-300 rounded-md px-2 py-1.5 text-sm"
          />
        </Field>
        <Field label="Consultation avg minutes (used by clinic-time auto-derivation in Phase 6.2)">
          <input
            type="number" step="1" min="1"
            value={form.consultation_avg_minutes}
            onChange={(e) => setF('consultation_avg_minutes', e.target.value)}
            className="w-full border border-sand-300 rounded-md px-2 py-1.5 text-sm"
          />
        </Field>
        <Field label="Daily meal allowance (PGK)">
          <input
            type="number" step="1" min="0"
            value={form.daily_meal_allowance_pgk}
            onChange={(e) => setF('daily_meal_allowance_pgk', e.target.value)}
            className="w-full border border-sand-300 rounded-md px-2 py-1.5 text-sm"
          />
        </Field>
        <Field label="Notes (why these rates were chosen)">
          <textarea
            value={form.notes}
            onChange={(e) => setF('notes', e.target.value)}
            rows={3}
            className="w-full border border-sand-300 rounded-md px-2 py-1.5 text-sm"
          />
        </Field>

        {error && (
          <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
            {error?.response?.data?.detail || error?.message || 'Save failed.'}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-3 py-1.5 rounded-md border border-sand-300 text-sm text-slate-600 hover:bg-sand-50"
          >Cancel</button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-3 py-1.5 rounded-md bg-wantok-600 text-white text-sm hover:bg-wantok-700 disabled:opacity-60"
          >{isSubmitting ? 'Saving…' : 'Save rates'}</button>
        </div>
      </form>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// Modal shell (intentionally simple — no portal, no focus trap; admin
// surface is power-user, not patient-facing).
// ---------------------------------------------------------------------------

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-slate-900/40 backdrop-blur-sm overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-xl border border-sand-200 w-full max-w-lg mt-12 p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-semibold text-slate-900">{title}</h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 text-xl leading-none"
            aria-label="Close"
          >×</button>
        </div>
        {children}
      </div>
    </div>
  )
}
